/**
 * MODULE: _shared/frontend-shell/src/features/crm/constants/crmDealStages.ts
 * PURPOSE: Single source of truth for CRM deal stage slugs, order, display labels, and legacy slug normalization
 * DEPENDENCIES: ../types/opportunity.types (OpportunityStage)
 * PUBLIC_API: CRM_OPEN_PIPELINE_STAGE_ORDER, CRM_FUNNEL_STAGE_ORDER, CRM_DEAL_STAGE_LABELS, normalizeDealStageSlug, nextFunnelStageAfter
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 92/150
 * RECENT: 2026-05-08 [2.2.889] **`nextFunnelStageAfter`** for bulk stage advance — root **`CHANGELOG` [2.2.889]`**
 * RECENT_PRIOR: 2026-05-01 [2.2.778] — Shared stage order and labels for CRM deal UI; root **`CHANGELOG` [2.2.778]`**
 */

import type { OpportunityStage } from '../types/opportunity.types';

/** Open pipeline only (excludes terminal stages). */
export const CRM_OPEN_PIPELINE_STAGE_ORDER = [
  'prospecting',
  'discovery',
  'qualified',
  'use_cases',
  'tech_workshop',
  'eb_meeting',
  'bva',
  'eb_playback',
  'pov',
  'paper_process',
  'negotiate_close',
] as const satisfies readonly OpportunityStage[];

export const CRM_FUNNEL_STAGE_ORDER: readonly OpportunityStage[] = [
  ...CRM_OPEN_PIPELINE_STAGE_ORDER,
  'closed_won',
  'closed_lost',
];

/** Next stage in **`CRM_FUNNEL_STAGE_ORDER`**, or **`null`** when already terminal (**`closed_won`** or **`closed_lost`**). */
export function nextFunnelStageAfter(current: OpportunityStage): OpportunityStage | null {
  if (current === 'closed_won' || current === 'closed_lost') return null;
  const order = CRM_FUNNEL_STAGE_ORDER as readonly OpportunityStage[];
  const idx = order.indexOf(current);
  if (idx < 0) return null;
  return order[idx + 1] ?? null;
}

export const CRM_DEAL_STAGE_LABELS: Record<OpportunityStage, string> = {
  prospecting: 'Prospecting',
  discovery: 'Discovery',
  qualified: 'Qualified',
  use_cases: 'Use Cases',
  tech_workshop: 'Tech Workshop',
  eb_meeting: 'EB Meeting',
  bva: 'BVA',
  eb_playback: 'EB Playback',
  pov: 'PoV',
  paper_process: 'Paper Process',
  negotiate_close: 'Negotiate & Close',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

/** Map historical API or DB slugs to the current canonical slug. */
const LEGACY_DEAL_STAGE_SLUG_TO_CANONICAL: Record<string, OpportunityStage> = {
  qualification: 'qualified',
  needs_analysis: 'discovery',
  proposal: 'use_cases',
  negotiation: 'negotiate_close',
  commitment: 'negotiate_close',
  evaluation: 'pov',
};

/**
 * Normalize any stage string (slug, legacy slug, or rough NL) to a canonical OpportunityStage.
 */
export function normalizeDealStageSlug(raw: string | undefined): OpportunityStage {
  let key = (raw ?? '').trim().toLowerCase();
  if (!key) return 'prospecting';
  key = key.replace(/&/g, 'and');
  key = key.replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  const legacy = LEGACY_DEAL_STAGE_SLUG_TO_CANONICAL[key];
  if (legacy) return legacy;
  if (key === 'negotiate_and_close') return 'negotiate_close';
  if (key === 'proof_of_value' || key === 'proofofvalue') return 'pov';
  const valid = new Set(CRM_FUNNEL_STAGE_ORDER as readonly string[]);
  if (valid.has(key)) return key as OpportunityStage;
  return 'prospecting';
}

/** Select / bulk-action rows for every funnel stage (no empty “all” row). */
export const CRM_DEAL_STAGE_SELECT_OPTIONS: { value: OpportunityStage; label: string }[] = CRM_FUNNEL_STAGE_ORDER.map(
  (value) => ({ value, label: CRM_DEAL_STAGE_LABELS[value] })
);
